import React from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Stack,
  Chip,
  IconButton,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  CalendarToday,
  AccessTime,
  Visibility,
  Category,
  Repeat,
  Groups,
  Close as CloseIcon,
  Description,
} from "@mui/icons-material";

const PRIMARY = "rgb(35, 71, 159)";

const EventInfo = ({
  event = {},
  open,
  handleClose,
  handleEdit,
  handleDelete,
  disable,
}) => {
  if (!event) return null;

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString(undefined, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatTime = (time) => {
    if (!time) return "";
    const [h, m] = time.split(":");
    const d = new Date();
    d.setHours(h, m);
    return d.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const capitalize = (text) =>
    text ? text.charAt(0).toUpperCase() + text.slice(1) : "-";

  // Reusable Info Block for Grid items
  const InfoBlock = ({ icon: Icon, label, value, subValue }) => (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box sx={{ mt: 0.3, color: PRIMARY }}>
        <Icon sx={{ fontSize: "20px", opacity: 0.8 }} />
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: "11px",
            color: "#888",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            mb: 0.2,
          }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#222" }}>
          {value}
        </Typography>
        {subValue && (
          <Typography sx={{ fontSize: "13px", color: "#666", mt: 0.2 }}>
            {subValue}
          </Typography>
        )}
      </Box>
    </Stack>
  );

  return (
    <Modal open={open} onClose={handleClose} closeAfterTransition>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "94%", md: 650 },
          bgcolor: "#fff",
          borderRadius: "24px",
          boxShadow: "0 30px 90px rgba(0,0,0,0.22)",
          overflow: "hidden",
          outline: "none",
        }}
      >
        {/* HEADER SECTION */}
        <Box sx={{ px: 4, pt: 4, pb: 3, position: "relative" }}>
          <IconButton
            onClick={handleClose}
            sx={{ position: "absolute", right: 16, top: 16, color: "#999" }}
          >
            <CloseIcon />
          </IconButton>

          <Typography
            sx={{
              fontSize: "26px",
              fontWeight: 800,
              color: "#111",
              mb: 1.5,
              pr: 4,
              lineHeight: 1.2,
            }}
          >
            {capitalize(event.title)}
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              icon={
                <Visibility
                  sx={{
                    fontSize: "16px !important",
                    color: `${PRIMARY} !important`,
                  }}
                />
              }
              label={capitalize(event.visibility)}
              sx={{
                backgroundColor: "rgba(35,71,159,0.08)",
                color: PRIMARY,
                fontWeight: 700,
                borderRadius: "8px",
              }}
            />
            <Chip
              label={event.allDay ? "All Day" : "Scheduled"}
              variant="outlined"
              sx={{ fontWeight: 600, borderRadius: "8px", borderColor: "#ddd" }}
            />
          </Stack>
        </Box>

        <Divider sx={{ mx: 4, opacity: 0.6 }} />

        {/* DETAILS SECTION */}
        <Box sx={{ px: 4, py: 4 }}>
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <InfoBlock
                icon={CalendarToday}
                label="Date & Time"
                value={formatDate(event.startDate)}
                subValue={
                  !event.allDay
                    ? `${formatTime(event.startTime)}${
                        event.endTime ? ` – ${formatTime(event.endTime)}` : ""
                      }`
                    : "All Day Event"
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <InfoBlock
                icon={Category}
                label="Event Type"
                value={capitalize(event?.events?.label)}
              />
            </Grid>

            {event.recurrence?.frequency !== "none" && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoBlock
                  icon={Repeat}
                  label="Recurrence"
                  value={capitalize(event.recurrence?.frequency)}
                />
              </Grid>
            )}

            {/* AUDIENCE CHIPS SECTION */}
            <Grid size={{ xs: 12 }}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box sx={{ mt: 0.3, color: PRIMARY }}>
                  <Groups sx={{ fontSize: "20px", opacity: 0.8 }} />
                </Box>
                <Box sx={{ width: "100%" }}>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: "#888",
                      fontWeight: 700,
                      mb: 1,
                      textTransform: "uppercase",
                    }}
                  >
                    Target Audience
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {/* Participants List */}
                    {event.visibility === "user" &&
                      event.participants?.map((p, i) => (
                        <Chip
                          key={i}
                          label={p?.label}
                          size="small"
                          sx={{ bgcolor: "#f0f2f5", fontWeight: 500 }}
                        />
                      ))}
                    {/* Academic Year List */}
                    {event.visibility === "academicYear" &&
                      event.academicYears?.map((a, i) => (
                        <Chip
                          key={i}
                          label={a?.label}
                          size="small"
                          sx={{ bgcolor: "#f0f2f5", fontWeight: 500 }}
                        />
                      ))}
                    {/* Class Section List */}
                    {event.visibility === "classSection" &&
                      event.classSections?.map((c, i) => (
                        <Chip
                          key={i}
                          label={c?.label}
                          size="small"
                          sx={{ bgcolor: "#f0f2f5", fontWeight: 500 }}
                        />
                      ))}
                    {/* Placeholder if none */}
                    {!(
                      event.participants?.length ||
                      event.academicYears?.length ||
                      event.classSections?.length
                    ) && (
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color: "#bbb",
                          fontStyle: "italic",
                        }}
                      >
                        No specific audience assigned
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Grid>
          </Grid>

          {/* DESCRIPTION BOX */}
          <Box
            sx={{
              p: 3,
              borderRadius: "16px",
              background: "linear-gradient(145deg, #fafbff, #f4f7ff)",
              border: "1px solid #eef2ff",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
              <Description sx={{ fontSize: "18px", color: "#888" }} />
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "#888",
                  textTransform: "uppercase",
                }}
              >
                Description
              </Typography>
            </Stack>

            <Box
              sx={{
                fontSize: "15px",
                color: "#444",
                lineHeight: 1.7,
                "& p": { m: 0 },
              }}
              dangerouslySetInnerHTML={{
                __html:
                  event.message ||
                  "<p style='color: #bbb'>No description provided for this event.</p>",
              }}
            />
          </Box>
        </Box>

        {/* FOOTER ACTIONS */}
        {disable && (
          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            sx={{
              px: 4,
              py: 3,
              bgcolor: "#fcfcfc",
              borderTop: "1px solid #eee",
            }}
          >
            <Button
              variant="text"
              color="error"
              onClick={handleDelete}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                px: 2,
                borderRadius: "10px",
              }}
            >
              Delete Event
            </Button>

            <Box sx={{ flexGrow: 1 }} />

            <Button
              variant="outlined"
              onClick={handleEdit}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                borderColor: PRIMARY,
                color: PRIMARY,
                fontWeight: 700,
                px: 3,
                "&:hover": {
                  borderColor: PRIMARY,
                  bgcolor: "rgba(35,71,159,0.04)",
                },
              }}
            >
              Edit
            </Button>

            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                backgroundColor: PRIMARY,
                fontWeight: 700,
                px: 4,
                boxShadow: `0 8px 20px rgba(35,71,159,0.25)`,
                "&:hover": {
                  backgroundColor: "rgb(25,55,130)",
                  boxShadow: `0 10px 25px rgba(35,71,159,0.35)`,
                },
              }}
            >
              Close
            </Button>
          </Stack>
        )}
      </Box>
    </Modal>
  );
};

export default EventInfo;
